import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Loader2, AlertTriangle, FileX2, Play, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BulkNoindexPage() {
  const [data, setData] = useState({ sites: [], total_recommended: 0, total_applied: 0,
                                      total_remaining: 0, overall_progress_pct: 0 });
  const [loading, setLoading] = useState(true);
  const [patchingId, setPatchingId] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/noindex/progress`);
      setData(res.data);
    } catch (e) {
      toast.error("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchSite = async (siteId, name, limit = 20) => {
    setPatchingId(siteId);
    try {
      const res = await axios.post(`${API}/admin/noindex/apply/${siteId}?limit=${limit}`);
      if (res.data.ok) {
        toast.success(`✓ ${name}: ${res.data.patched}/${res.data.checked} patched (${res.data.failed} failed)`);
        await load();
      } else {
        toast.error(`✗ ${name}: ${res.data.error}`);
      }
    } catch (e) {
      toast.error("Patch failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setPatchingId(null);
    }
  };

  const bulkPatchAll = async () => {
    if (!window.confirm(`Patchować 20 postów na ${data.sites.length} portalach? (max ${20 * data.sites.length} postów)`)) return;
    setBulkRunning(true);
    let total = 0, fail = 0;
    for (const s of data.sites) {
      if (s.remaining === 0) continue;
      try {
        const res = await axios.post(`${API}/admin/noindex/apply/${s.site_id}?limit=20`);
        total += res.data.patched || 0;
        fail += res.data.failed || 0;
      } catch (_) { fail += 1; }
      await new Promise(r => setTimeout(r, 1500));
    }
    toast.success(`Bulk done: ${total} patched, ${fail} failed`);
    setBulkRunning(false);
    await load();
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <FileX2 className="w-8 h-8 text-red-600" />
          <div>
            <h1 className="text-2xl font-bold">Bulk Noindex Low-Quality</h1>
            <p className="text-sm text-muted-foreground">Posty score≤25 → noindex,follow (oszczędza crawl budget Google)</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Odśwież
          </Button>
          <Button onClick={bulkPatchAll} disabled={bulkRunning || loading || data.total_remaining === 0}
                  className="bg-red-600 hover:bg-red-700">
            {bulkRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
            Patch ALL (20/portal)
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-blue-700 uppercase">Łącznie do oznaczenia</p>
            <p className="text-2xl font-bold text-blue-900">{data.total_recommended.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-emerald-700 uppercase">Już oznaczone</p>
            <p className="text-2xl font-bold text-emerald-900">{data.total_applied.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-amber-700 uppercase">Pozostało</p>
            <p className="text-2xl font-bold text-amber-900">{data.total_remaining.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-purple-700 uppercase">Postęp</p>
            <p className="text-2xl font-bold text-purple-900">{data.overall_progress_pct}%</p>
            <div className="w-full bg-purple-200 rounded-full h-2 mt-2">
              <div className="bg-purple-600 h-2 rounded-full" style={{width: `${data.overall_progress_pct}%`}} />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            Portale wg liczby śmieci do oznaczenia
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {data.sites.map((s) => (
              <div key={s.site_id} className="px-4 py-3 flex items-center gap-4 hover:bg-slate-50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{s.name}</span>
                    {s.remaining === 0 && <Badge className="bg-emerald-200 text-emerald-900">✓ DONE</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{s.url}</div>
                  <div className="flex items-center gap-3 mt-1 text-xs">
                    <span className="text-emerald-700">✓ {s.applied}</span>
                    <span className="text-amber-700">⏳ {s.remaining} pozostało</span>
                    <span className="text-slate-500">{s.progress_pct}%</span>
                    <div className="flex-1 max-w-[200px] bg-slate-200 rounded-full h-1.5">
                      <div className="bg-emerald-500 h-1.5 rounded-full" style={{width: `${s.progress_pct}%`}} />
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={s.remaining > 0 ? "default" : "ghost"}
                  disabled={patchingId === s.site_id || s.remaining === 0}
                  onClick={() => patchSite(s.site_id, s.name, 20)}
                  className={s.remaining > 0 ? "bg-red-600 hover:bg-red-700" : ""}
                >
                  {patchingId === s.site_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  {s.remaining > 0 ? `Patch 20` : "—"}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground text-center">
        Auto-cron poniedziałek 04:00 — patch 20/portal × 30 portali = 600/tydz. ETA ~{Math.ceil(data.total_remaining / 600)} tygodni.
      </div>
    </div>
  );
}
